import { loadRemoteModule } from '@angular-architects/module-federation-runtime';
import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Route, Router, Routes } from '@angular/router';
import { combineLatest, firstValueFrom } from 'rxjs';
import { MfeAppConfig } from 'src/app/app.environment';
import { CssLoaderGuard } from 'src/app/guards/css-loader.guard';
import { LoginGuard } from 'src/app/guards/login.guard';
import { MaintenanceComponent } from 'src/app/maintenance/maintenance.component';
import { NotfoundComponent } from 'src/app/notfound/notfound.component';
import { EnvironmentService } from '../environment/environment.service';
import { LoaderService } from '../loader.service';
import { UserecoApiService } from '../usereco-api.service';

const LAST_MFE_PATH_KEY = 'lastMfePath';

/**
 * The routing system implemented here is unncessarily complicated due to:
 * - Multiple changes in the routing requirements when the MFE sytem was being created
 * - Possibility of needing to support a different routing system (which is already mostly supported here)
 * - The inherent complexity of the final routing requirements
 */
@Injectable({
  providedIn: 'root'
})
export class RouteService {
  /**
   * This can be removed once we're sure that we'll never need an alias for each MFE
   */
  private readonly mfesByHostName: Map<string, MfeAppConfig> = new Map();

  private readonly mfesByPath: Map<string, MfeAppConfig> = new Map();

  /**
   * This should be accessed through getRootMFE method
   */
  private rootMFE: MfeAppConfig | undefined;

  constructor(private environmentService: EnvironmentService) {}

  get useRootPath() {
    return this.environmentService.get('useRootPath');
  }

  /**
   * Assuming useRootPath is true:
   * If the path begins with an MFE's prefix/path, the MFE will be loaded.
   * If the path is the root path, it will be redirected to the root MFE path.
   * If the path is a session path, it will be redirected to the last MFE visited.
   * If the path does not begin with any of the MFE prefixes, it will be redirected to the root MFE path.
   * If the navigation.extra.state has an 'mfe' property, it will be redirected to that MFE.
   * Otherwise, notfound component will be shown.
   *
   * The root MFE prefix will be removed via the custom url serializer which will produce the illusion of having no prefix for the root MFE.
   * The root MFE is the MFE whose alias host names match the current host name.
   * It's currently set to the CLU MFE.
   */
  createRoutes(): Routes {
    this.ensureHostNamesMapInitialized();

    return [
      ...this.createMaintenanceModeRoutes(),
      ...this.createMFERoutes(),
      this.createRedirectRouteForRootPath(),
      this.createRedirectRouteForSessionPaths(),
      this.createRedirectRouteForRootMFE(),
      this.createWildCardRoute()
    ];
  }

  private createMaintenanceModeRoutes(): Routes {
    return this.environmentService.getAppsArray().map((app) => {
      const route: Route = {
        path: `${app.path}/maintenance`,
        component: MaintenanceComponent,
        canActivate: [
          () => {
            // We have to do this here since the router outlet is hidden if the loader is shown (TODO something we might need to change)
            inject(LoaderService).displayLoaderSubject.next(false);
            return true;
          }
        ]
      };

      return route;
    });
  }

  private createMFERoutes(): Routes {
    const appRoutes: Routes = [];

    for (const app of this.environmentService.getLoadedApps()) {
      if (app.isRootApp) {
        this.rootMFE = app;
      }

      this.mfesByPath.set(app.path, app);

      if (app.path !== 'compose') {
        appRoutes.push(this.createRouteForMfe(app));
      } else if (app.path === 'compose') {
        appRoutes.push(this.createRouteForNestedMFE(app));
      }
    }

    return appRoutes;
  }

  /**
   * @returns The MFE app that will be loaded in the rooth path (determined by comparing the aliastHostNames of each MFE with the current host name).
   */
  getRootMFE(): MfeAppConfig | undefined {
    if (!this.useRootPath) {
      return undefined;
    }

    if (this.rootMFE) {
      return this.rootMFE;
    }

    this.ensureHostNamesMapInitialized();
    return this.mfesByHostName.get(window.location.hostname);
  }

  /**
   * MFE apps are loaded in their app.path.
   * However, if useRootPath is enabled, the root MFE (or the MFE whose aliasHostNames match the current hostname)
   * will have its prefix/path removed using our custom UrlSerializer. This will make it seem that such root MFE
   * is being loaded in the root path without a prefix.
   */
  private createRouteForMfe(app: MfeAppConfig): Route {
    return {
      path: app.path,
      canActivate: [
        () => {
          const apiService = inject(UserecoApiService);
          const router = inject(Router);

          const obs = combineLatest([
            apiService.getMaintenanceMode(undefined),
            apiService.getMaintenanceMode(app.path)
          ]);
          return firstValueFrom(obs).then(
            ([isMaintenanceModeShell, isMaintenanceModeMFE]) => {
              if (isMaintenanceModeShell || isMaintenanceModeMFE) {
                router.navigate(['/', app.path, 'maintenance']);
                return false;
              }
              return true;
            }
          );
        },
        LoginGuard,
        CssLoaderGuard,
        () => {
          RouteService.saveLastMFEPath(app.path);
          return true;
        }
      ],
      loadChildren: () =>
        loadRemoteModule({
          type: 'module',
          remoteEntry: `${app.hostname}/remoteEntry.js`,
          exposedModule: './MfeModule'
        }).then((m) => m.MfeModule)
    };
  }

  /**
   * Used for the reporting MFE which is nested inside the CLU MFE
   */
  private createRouteForNestedMFE(app: MfeAppConfig): Route {
    return {
      path: app.path,
      loadChildren: () =>
        loadRemoteModule({
          type: 'module',
          remoteEntry: `${app.hostname}/remoteEntry.js`,
          exposedModule: './MfeModule'
        }).then((m) => m.MfeModule),
      outlet: app.outlet
    };
  }

  /**
   * This will effectively treat all paths that don't match any of the MFE prefixes to be subpaths of the root MFE
   */
  private createRedirectRouteForRootMFE(): Route {
    return {
      matcher: (url) => {
        const prefix = url[0]?.path;
        if (!this.useRootPath || this.mfesByPath.has(prefix)) {
          return null;
        }

        // Match if prefix doesn't match any of the MFE prefixes, meaning that it's a subpath of the root MFE
        return { consumed: url };
      },
      component: NotfoundComponent,
      canActivate: [
        (route: ActivatedRouteSnapshot) => {
          RouteService.navigateToMfe(inject(Router), this.getRootMFE(), route);
          return false;
        }
      ]
    };
  }

  /**
   * Session paths will be redirected to the last MFE visited.
   */
  private createRedirectRouteForSessionPaths(): Route {
    return {
      path: 'session/:subpath',
      canActivate: [
        (route: ActivatedRouteSnapshot) => {
          const router = inject(Router);
          // Let the individual mfe handle session routes
          const mfePath =
            sessionStorage.getItem(LAST_MFE_PATH_KEY) ||
            this.environmentService.getDefaultApp().path;
          const lastMfeApp = this.mfesByPath.get(mfePath);
          RouteService.navigateToMfe(router, lastMfeApp, route);
          return false;
        }
      ],
      component: NotfoundComponent
    };
  }

  /**
   * The empty root path will be redirected to the root MFE, or if useRootPath is disabled, to the default MFE
   */
  private createRedirectRouteForRootPath(): Route {
    return {
      path: '',
      pathMatch: 'full',
      redirectTo: this.useRootPath
        ? this.getRootMFE()?.path
        : RouteService.retrieveLastMFEPath() ||
          this.environmentService.getDefaultApp().path
    };
  }

  /**
   * Edge cases are handled here, like when doing imperative navigation with extra MFE state (as we're doing in CLU).
   * If not handled, the notfound component will be shown.
   */
  private createWildCardRoute(): Route {
    return {
      path: '**',
      component: NotfoundComponent,
      canActivate: [
        (route: ActivatedRouteSnapshot) => {
          const router = inject(Router);
          const navigation = router.getCurrentNavigation();
          const mfePath = navigation?.extras.state?.['mfe'];
          if (mfePath) {
            const mfeApp = this.mfesByPath.get(mfePath);
            RouteService.navigateToMfe(router, mfeApp, route);
            return false;
          } else {
            return true;
          }
        }
      ]
    };
  }

  private ensureHostNamesMapInitialized() {
    if (!this.useRootPath || !!this.mfesByHostName.size) {
      return;
    }

    this.mfesByHostName.clear();
    for (const app of this.environmentService.getLoadedApps()) {
      const hostnames =
        app.aliasHostNames?.split(',')?.map((x) => x.trim()) ?? [];
      for (const hostname of hostnames) {
        if (hostname.trim()) {
          this.mfesByHostName.set(hostname.trim(), app);
        }
      }
    }
  }

  private static navigateToMfe(
    router: Router,
    mfeApp: MfeAppConfig | undefined,
    route: ActivatedRouteSnapshot
  ) {
    router.navigate([mfeApp?.path, ...route.url.map((x) => x.path)], {
      queryParams: route.queryParams,
      queryParamsHandling: 'merge'
    });
  }

  private static saveLastMFEPath(path: string) {
    sessionStorage.setItem(LAST_MFE_PATH_KEY, path);
  }

  private static retrieveLastMFEPath(): string | null {
    return sessionStorage.getItem(LAST_MFE_PATH_KEY);
  }
}
